// extendTheme.js
const lightColorScheme = {
  // global typography settings – anything that uses MUI's
  // Typography component (or inherits from it) will pick up
  // this font stack automatically. you no longer need to add
  // `fontFamily` in every sx prop.
  typography: {
    fontFamily: "Space Grotesk, Inter, system-ui, sans-serif",
    h1: {
      color: "#ffffffff",
    },
    h2: {
      color: "#ffffffff",
    },
    h3: {
      color: "#3784b4ff",
    },
    h4: {
      color: "#ffffffff",
    },
    h5: {
      color: "#000000ff",
    },
    h6: {
      color: "#000000ff",
    },
    h7: {
      color: "#ffffffff",
      fontWeight: 600,
    },
    body2: {
      color: "#ffffffff",
    },
    overline: {
      color: "#008cffff",
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "#FFF",
          boxShadow: "0px 2px 4px -1px #f2f2f2, 0px 4px 5px 0px #f2f2f2, 0px 1px 10px 0px #f2f2f2;",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          textTransform: "none",
          letterSpacing: 1.2,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 50,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: "0px 2px 4px -1px #f2f2f2, 0px 4px 5px 0px #f2f2f2, 0px 1px 10px 0px #f2f2f2;",
        },
      },
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#005aa3", // --tw-gradient-from
      light: "#47ade9",
      dark: "#004a80", // --tw-gradient-to
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#FCD34D",
      light: "#ffee30",
      dark: "#b45309",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
      section: "#f1f5f9",
      service: 'hsl(210 15% 93% / 0.5)',
      footer: "hsl(213 30% 15%)",
      chip: "#e3f2fd",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
      white: "#ffffff",
      link: "#005f8d",
      heroSecondary: "#c1e8f5",
    },
    custom: {
      // Gradients
      heroGradient: `radial-gradient(ellipse 100% 30% at 50% 100%, rgba(255, 255, 255, 0.45) 50%, transparent 100%),
                     linear-gradient(135deg, hsl(207 100% 32%) 0%, hsl(195 95% 43%) 50%, hsl(207 100% 25%) 100%)`,
      accentGradient: "linear-gradient(135deg, hsl(66, 100%, 91%) 0%, hsl(64, 100%, 73%) 50%, hsl(66, 94%, 87%) 100%)",
      buttonGradient: "linear-gradient(to right, hsl(207 100% 32%), hsl(195 95% 43%))",

      //Project State Shadow
      activeBlueShadow: '0 8px 20px rgba(0, 90, 163, 0.45)',

      // Hero Specifics
      heroBadgeBg: "rgba(71, 173, 233, 0.42)",
      heroBorder: "rgba(238, 238, 238, 0.9)",
      glassBorder: "rgba(238, 238, 238, 0.4)",
      statCardBg: "rgba(255, 255, 255, 0.1)",
      statHover: "rgba(228, 228, 228, 0.2)",

      // Components
      chipText: "#005aa3",
      tagBg: "hsl(61 77% 58% / 0.2)",
      tagText: "hsl(213 30% 15%)",
      cardShadow: "0 4px 20px rgba(0,0,0,0.05)",
      cardShadowHover: "0 14px 30px rgba(0,0,0,0.12)",
      overlay: "rgba(0, 0, 0, 0.4)",

      // View All Projects Button
      viewAllBtnBorder: "#005aa3",
      viewAllBtnText: "#005aa3",
      viewAllBtnBg: "#ffffff",
      viewAllBtnHoverBg: "#005aa3",
      viewAllBtnHoverText: "#ffffff",

      // Mobile Menu
      mobileMenuBg: "linear-gradient(180deg, #e3f2fd 0%, #bbdefb 100%)",
      mobileMenuText: "#005aa3",
      mobileMenuBtnText: "#ffffff",

      // Services Section
      serviceIconBg: 'linear-gradient(to right, hsl(207 100% 32%), hsl(195 95% 43%))',
      serviceIconColor: "#ffffff",

      // Navbar
      navbarBgScrolled: "rgba(255, 255, 255, 0.7)",
      navbarTextScrolled: "hsl(213 30% 15%)",

      // Footer
      footerText: "rgba(255, 255, 255, 0.7)",
      footerTitle: "#ffffff",
      footerIconBg: "rgba(255, 255, 255, 0.1)",
      footerIconColor: "rgba(255, 255, 255, 0.85)",
      footerLinkHover: "#fbff00ff",
      footerDivider: "rgba(255, 255, 255, 0.12)",
      footerCopyright: "rgba(255, 255, 255, 0.55)",
    },
    // Retaining legacy colors just in case to avoid crashes, mapped to new system where reasonable
    inactive_text: { main: "#BFD4E4" },
    notactive_text: { main: "#B5B7C0" },
    table_text: { main: "#444A6D" },
    table_border: { main: "#DDEAF3" },
    thead_bg: { main: "#F2F7F9" },
    boxBg: { main: "#F3F6F9" },
    boxBorder: { main: "#CCCCCC" },
    white: { main: "#FFF", light: "#FFF", dark: "#FFF" },
    info: { main: "#6699FF" },
    navy: { main: "#00B6D5" },
    danger: { main: "#d32f2f" },
  },
};

const darkColorScheme = {
  typography: {
    fontFamily: "CameraPlainVariable",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    h1: {
      color: "#FF6600",
    },
    h2: {
      fontSize: "1.5rem",
    },
  },
  palette: {
    mode: "dark",
    primary: {
      main: "#FF6600",
    },
    secondary: {
      main: "#000099",
    },
    textPrimary: {
      main: "#6699FF",
    },
    textSecondary: {
      main: "#00B6D5",
    },
  },
};

const modalBox = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  maxHeight: "80%",
  overflow: "auto",
  bgcolor: "background.paper",
  borderRadius: 5,
  outline: "none",
  boxShadow: 5,
  p: 4,
};

export { lightColorScheme, darkColorScheme, modalBox };
